import React, { FC } from "react";
import { Button } from "antd";
import { Work, TeamMembershipIds } from "../../data-types";
import { Icons } from "../vpd-icons/icons";
import { TimeDurationInput } from "../time-duration-input";
import { TeamMemberPicker } from "../team-member-picker";
import styles from "./fee-earners-row.module.css";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
import { TeamMembershipPartsFragment } from "~/gql/graphql";
const {
  ["button-container"]: buttonContainerClassName,
  ["container"]: containerClassName,
  ["form"]: formClassName,
  ["fee"]: feeClassName,
  ["to"]: toClassName,
  ["label"]: labelClassName,
  ["anticpated-time"]: anticpatedTimeClassName,
} = styles as any;

type Props = {
  work: Work;
  onUpdateWork: (work: Work, timeEntryUpdate: boolean) => void;
  onDeleteWork: (id: number) => void;
  range?: boolean;
  currencySymbol: string;
  teamMembers: TeamMembershipPartsFragment[];
  includedTeamMemberships: TeamMembershipIds[];
  matterId: number;
};

export const FeeEarnerRow: FC<Props> = ({
  work,
  onUpdateWork,
  onDeleteWork,
  range = false,
  currencySymbol,
  teamMembers,
  includedTeamMemberships,
  matterId,
}) => {
  const lowerTimePropertyName = range ? "timeLower" : "timeSingle";

  const onTimeChange = (value: number, type: "timeLower" | "timeUpper", work: Work) => {
    const updatePropertyName = type === "timeLower" ? lowerTimePropertyName : "timeUpper";
    onUpdateWork(
      {
        ...work,
        [updatePropertyName]: value,
      },
      true
    );
  };

  const taxonomy = useTaxonomyLabel();

  return (
    <div className={containerClassName}>
      <div className={formClassName}>
        <div>
          <span className={labelClassName}>{taxonomy.FeeEarner} and Rate:</span>
          <TeamMemberPicker
            teamMembers={teamMembers}
            includedTeamMemberships={includedTeamMemberships}
            selectedRate={work.selectedRate}
            selectedFeeEarner={work.feeEarner ?? null}
            currencySymbol={currencySymbol}
            onChange={(values) => {
              if (values.selectedRateId) {
                return onUpdateWork({ ...work, ...values }, false);
              }
            }}
            matterId={matterId}
          />
        </div>
        <div>
          <div className={anticpatedTimeClassName}>
            <span className={labelClassName}>Anticipated time:</span>
            <span>
              <TimeDurationInput
                defaultValue={work[lowerTimePropertyName] ?? 0}
                maxMinutes={work.timeUpper}
                minMinutes={null}
                onChange={(value) => onTimeChange(value, "timeLower", work)}
              />
            </span>
            {range && (
              <>
                <span className={toClassName}>to</span>
                <span>
                  <TimeDurationInput
                    defaultValue={work.timeUpper ?? undefined}
                    minMinutes={work.timeLower}
                    maxMinutes={null}
                    onChange={(value) => onTimeChange(value, "timeUpper", work)}
                  />
                </span>
              </>
            )}
          </div>
          <div>
            <span className={feeClassName}>
              <span className={labelClassName}>Fee:</span>
              {calculatePriceText(work.price, range, currencySymbol)}
            </span>
          </div>
        </div>
      </div>
      <div className={buttonContainerClassName}>
        <Button
          size="small"
          shape="circle"
          icon={Icons.Delete}
          type="link"
          onClick={() => onDeleteWork(work.id)}
        />
      </div>
    </div>
  );
};

const calculatePriceText = (
  price: { lower?: number | null; upper?: number | null; single?: number | null },
  range: boolean,
  currencySymbol: string
): string => {
  return range
    ? `${currencySymbol}${(price.lower || 0).toLocaleString()} to ${currencySymbol}${(
        price.upper || 0
      ).toLocaleString()}`
    : `${currencySymbol}${(price.single || 0).toLocaleString()}`;
};
